import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  REVIEW_RULE_SECTIONS,
  type ReviewRuleCategoryConfig,
} from '~/components/contracts/reviewRuleCategories';
import type { ContractFormValues } from '~/components/contracts/types';
import { LAITextField } from '~/components/form';
import type { ReviewRuleCategory } from '~/data/schemas/documentReview';
import { BUTTON_SX } from '~/theme';

interface CategorySectionProps {
  config: ReviewRuleCategoryConfig;
  rows: { fieldId: string; index: number }[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMove: (index: number, category: ReviewRuleCategory) => void;
}

const CategorySection = ({ config, rows, onAdd, onRemove, onMove }: CategorySectionProps) => {
  const { control } = useFormContext<ContractFormValues>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
        <Typography variant="subtitle1">{config.label}</Typography>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          size="small"
          startIcon={
            <Add
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                padding: '2px',
              }}
            />
          }
          onClick={onAdd}
          sx={BUTTON_SX}
        >
          {config.addLabel}
        </Button>
      </Box>
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell width="65%">{config.ruleColumnLabel}</TableCell>
              <TableCell width="25%">Category</TableCell>
              <TableCell align="right" width="10%">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  None.
                </TableCell>
              </TableRow>
            ) : (
              rows.map(({ fieldId, index }) => (
                <TableRow key={fieldId}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <LAITextField
                      name={`reviewRules.${index}.reviewRule`}
                      fullWidth
                      type="text"
                      slotProps={{
                        input: {
                          placeholder: config.rulePlaceholder,
                          size: 'small',
                        },
                      }}
                      control={control}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {/* Moves the row to another section, keeping its text. */}
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={config.value}
                      onChange={(e) => onMove(index, e.target.value as ReviewRuleCategory)}
                      slotProps={{ htmlInput: { 'aria-label': `Category for rule ${index + 1}` } }}
                    >
                      {REVIEW_RULE_SECTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top', pt: 2 }}>
                    <IconButton
                      onClick={() => {
                        onRemove(index);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const ContractReviewRulesFormFields = () => {
  const { getValues } = useFormContext<ContractFormValues>();
  const { fields, append, remove, update } = useFieldArray<ContractFormValues>({
    name: 'reviewRules',
  });

  return (
    <Stack spacing={3}>
      {REVIEW_RULE_SECTIONS.map((config) => {
        const rows = fields
          .map((field, index) => ({ field, index }))
          .filter(({ field }) => {
            const category = (field as { category?: ReviewRuleCategory }).category;
            // Default category for legacy rows without one is "review-rule".
            return (category ?? 'review-rule') === config.value;
          })
          .map(({ field, index }) => ({ fieldId: field.id, index }));

        return (
          <CategorySection
            key={config.value}
            config={config}
            rows={rows}
            onAdd={() =>
              append({
                reviewRule: '',
                category: config.value,
              })
            }
            onRemove={remove}
            onMove={(index, category) =>
              // Read the live row, not `fields[index]`: `fields` lags the text
              // typed since the last array operation.
              update(index, { ...getValues(`reviewRules.${index}`), category })
            }
          />
        );
      })}
    </Stack>
  );
};
